import * as Sentry from '@sentry/react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { GENERIC_SERVER_ERROR } from 'api';
import Spinner from 'common/Spinner';
import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import StoreContext from 'contexts/storeContext';
import { useContext, useEffect, useState } from 'react';
import { DaysAndInterval, getDatesFromDaysAndInterval } from 'types/daysAndInterval';
import { StorePackMetric, StorePackMetricField } from 'types/storeMetrics';
import RefreshIndicator from 'common/RefreshIndicator';

type Props = {
  daysAndInterval: DaysAndInterval;
};

export type SortDirection = 'asc' | 'desc';

function TableHeaderCell({
  label,
  field,
  orderBy,
  order,
  handleSort,
}: {
  label: string;
  field: StorePackMetricField;
  orderBy: StorePackMetricField;
  order: SortDirection;
  handleSort: (field: StorePackMetricField) => (event: React.MouseEvent<unknown>) => void;
}) {
  const selected = orderBy === field;

  return (
    <TableCell sortDirection={selected ? order : false} sx={{ fontWeight: 600 }}>
      <TableSortLabel active={selected} direction={selected ? order : 'desc'} onClick={handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );
}
export default function DashboardPacksTable({ daysAndInterval }: Props) {
  const { api } = useContext(ApiContext);
  const { store, formatNumber } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [packMetrics, setPackMetrics] = useState<StorePackMetric[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [order, setOrder] = useState<SortDirection>('desc');
  const [orderBy, setOrderBy] = useState<StorePackMetricField>(StorePackMetricField.total_sum);

  useEffect(() => {
    const { startDate, endDate } = getDatesFromDaysAndInterval(daysAndInterval);
    setRefreshing(true);
    api.storePackMetrics
      .list(store.id, startDate, endDate, pageNumber, pageSize, order, orderBy)
      .then((res) => {
        setPackMetrics(res.data.results);
        setPageCount(res.data.count);
      })
      .catch((err) => {
        setError({ message: GENERIC_SERVER_ERROR });
        console.error(err);
        Sentry.captureException(err);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [api, store.id, daysAndInterval, setError, setLoading, setRefreshing, pageNumber, pageSize, order, orderBy]);

  const handleChangePage = (event: any, page: number) => {
    setPageNumber(page + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const rows = parseInt(event.target.value, 10);
    setPageSize(rows);
    setPageNumber(1);
  };

  const handleSort = (field: StorePackMetricField) => (event: React.MouseEvent<unknown>) => {
    const isDesc = orderBy === field && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(field);
  };

  return loading ? (
    <Spinner />
  ) : packMetrics.length === 0 ? (
    <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 14, my: 8 }}>No tienes datos en este período</Typography>
  ) : (
    <>
      <TableContainer sx={{ mt: 1 }}>
        <RefreshIndicator visible={refreshing} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '100%', fontWeight: 600 }}>Pack</TableCell>
              <TableHeaderCell
                label='Total'
                field={StorePackMetricField.total_sum}
                orderBy={orderBy}
                order={order}
                handleSort={handleSort}
              />
              <TableHeaderCell
                label='Unidades'
                field={StorePackMetricField.quantity_sum}
                orderBy={orderBy}
                order={order}
                handleSort={handleSort}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {packMetrics.map((pack) => (
              <TableRow key={pack.id}>
                <TableCell>{pack.name}</TableCell>
                <TableCell>${formatNumber(pack.total_sum)}</TableCell>
                <TableCell>{pack.quantity_sum}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component='div'
          count={pageCount}
          rowsPerPage={pageSize}
          page={pageNumber - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage='Packs por página:'
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
          size='small'
        />
      </TableContainer>
    </>
  );
}
